import {
    ArchiveBoxIcon,
    ArrowDownOnSquareIcon,
    ArrowPathIcon,
    ArrowUpOnSquareIcon,
    ArrowUturnLeftIcon,
    BuildingOffice2Icon,
    CalendarDaysIcon,
    CameraIcon,
    ChatBubbleLeftEllipsisIcon,
    CheckBadgeIcon,
    CheckCircleIcon,
    ClockIcon,
    CubeTransparentIcon,
    DocumentTextIcon,
    ExclamationTriangleIcon,
    ForwardIcon,
    HandThumbDownIcon,
    PauseCircleIcon,
    PencilSquareIcon,
    PlayCircleIcon,
    ShieldCheckIcon,
    ShoppingCartIcon,
    UserPlusIcon,
    XCircleIcon
} from '@heroicons/react/24/outline';
import type { ComponentType, ReactNode } from 'react';
import type { TimelineEvent } from '../actions/types';
import type { StatusCatalog } from '../../../services/statusCatalog';
import { EVENT_TYPE_TITLES, humanizeType } from './event-titles';
import {
    adminPatientUpdateBody,
    clarificationProvidedBody,
    designPlanBody,
    enterHoldBody,
    forceStatusBody,
    manufacturerStatusBody,
    manufacturerSubmissionBody,
    movePracticeBody,
    orthopulseOrderedBody,
    recordsResubmissionBody,
    refinementGoalBody,
    rejectionBody,
    retainerBody,
    retentionManufacturingBody,
    retentionRefusalBody,
    setPriorityBody
} from './event-body-formatters';
import {
    clinicalEscalationBody,
    clinicalEscalationTitle,
    handBackToDmBody,
    handBackToDmTitle,
    vaTriggeredBody
} from './virtual-appointment-event-formatters';

export interface EventToneClasses {
    ring: string;
    icon: string;
    dot: string;
}

export interface EventVisualConfig {
    icon: ComponentType<{ className?: string }>;
    tone: EventToneClasses;
    formatTitle: (event: TimelineEvent) => string;
    formatBody?: (event: TimelineEvent, catalog: StatusCatalog) => ReactNode;
}

const TONE_CLINICAL: EventToneClasses = {
    ring: 'border-blue-300 bg-blue-50',
    icon: 'text-blue-700',
    dot: 'bg-blue-500'
};

const TONE_INTERNAL: EventToneClasses = {
    ring: 'border-gray-300 bg-white',
    icon: 'text-gray-600',
    dot: 'bg-gray-400'
};

const TONE_APPROVAL: EventToneClasses = {
    ring: 'border-emerald-300 bg-emerald-50',
    icon: 'text-emerald-700',
    dot: 'bg-emerald-500'
};

const TONE_REQUEST: EventToneClasses = {
    ring: 'border-green-300 bg-green-50',
    icon: 'text-green-700',
    dot: 'bg-green-500'
};

const TONE_SCAN: EventToneClasses = {
    ring: 'border-orange-300 bg-orange-50',
    icon: 'text-orange-700',
    dot: 'bg-orange-500'
};

const TONE_FOLLOWUP: EventToneClasses = {
    ring: 'border-orange-300 bg-orange-50',
    icon: 'text-orange-700',
    dot: 'bg-orange-500'
};

const TONE_INTAKE: EventToneClasses = {
    ring: 'border-yellow-300 bg-yellow-50',
    icon: 'text-yellow-700',
    dot: 'bg-yellow-500'
};

const TONE_SCHEDULED: EventToneClasses = {
    ring: 'border-dashed border-gray-300 bg-white',
    icon: 'text-gray-400',
    dot: 'bg-gray-300'
};

const TONE_REJECTION: EventToneClasses = {
    ring: 'border-red-300 bg-red-50',
    icon: 'text-red-700',
    dot: 'bg-red-500'
};

const TONE_FALLBACK: EventToneClasses = {
    ring: 'border-gray-200 bg-white',
    icon: 'text-gray-400',
    dot: 'bg-gray-300'
};

function defaultTitle(event: TimelineEvent): string {
    const base = EVENT_TYPE_TITLES[event.type] ?? humanizeType(event.type);
    return event.version > 1 ? `${base} · v${event.version}` : base;
}

function monitoringTitle(event: TimelineEvent): string {
    const action = (event.payload as { action?: string } | undefined)?.action;
    const status = (event.payload as { status?: string } | undefined)?.status;
    const label = status ?? action;
    if (label) {
        return `Monitoring ${label.toString().toLowerCase()}`;
    }
    return defaultTitle(event);
}

export const EVENT_TYPE_VISUAL_CONFIG: Record<string, EventVisualConfig> = {
    patient_created: {
        icon: UserPlusIcon,
        tone: TONE_INTAKE,
        formatTitle: defaultTitle
    },
    case_submitted: {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    case_approved: {
        icon: CheckBadgeIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    case_declined: {
        icon: XCircleIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle
    },
    appointment_scheduled: {
        icon: CalendarDaysIcon,
        tone: TONE_SCHEDULED,
        formatTitle: defaultTitle
    },
    monitoring_started: {
        icon: PlayCircleIcon,
        tone: TONE_SCAN,
        formatTitle: defaultTitle
    },
    monitoring_state_changed: {
        icon: PauseCircleIcon,
        tone: TONE_SCAN,
        formatTitle: monitoringTitle
    },
    dm_comfort_scan_requested: {
        icon: CameraIcon,
        tone: TONE_SCAN,
        formatTitle: defaultTitle
    },
    dm_aligner_change_forced: {
        icon: ForwardIcon,
        tone: TONE_SCAN,
        formatTitle: defaultTitle
    },
    dm_forced_aligner_change_cancelled: {
        icon: ArrowUturnLeftIcon,
        tone: TONE_SCAN,
        formatTitle: defaultTitle
    },
    rdi_submission: {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    sdi_submission: {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    dx_report_submission: {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    followup_request: {
        icon: ChatBubbleLeftEllipsisIcon,
        tone: TONE_FOLLOWUP,
        formatTitle: defaultTitle
    },
    refinement_trigger: {
        icon: ArrowPathIcon,
        tone: TONE_REQUEST,
        formatTitle: defaultTitle
    },
    refinement_approved: {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    refinement_rejected: {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle
    },
    refinement_records_submitted: {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_INTAKE,
        formatTitle: defaultTitle
    },
    retention_trigger: {
        icon: ShieldCheckIcon,
        tone: TONE_REQUEST,
        formatTitle: defaultTitle
    },
    retention_approved: {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    retention_rejected: {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle
    },
    retention_records_submitted: {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_INTAKE,
        formatTitle: defaultTitle
    },
    treatment_closed: {
        icon: CubeTransparentIcon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle
    },
    // Automation: manufacturer (Spark) case-status change (internal visibility).
    manufacturer_status_updated: {
        icon: CubeTransparentIcon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle,
        formatBody: manufacturerStatusBody
    },
    // Workflow engine actions - keyed by the engine action id (kebab-case).
    'approve-records': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'reject-records': {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    'records-resubmitted': {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_INTAKE,
        formatTitle: defaultTitle,
        formatBody: recordsResubmissionBody
    },
    'create-in-manufacturer': {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle,
        formatBody: manufacturerSubmissionBody
    },
    'submit-design-plan': {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle,
        formatBody: designPlanBody
    },
    'design-pre-approved': {
        icon: CheckBadgeIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle,
        formatBody: designPlanBody
    },
    'plan-received': {
        icon: ArrowDownOnSquareIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    'approve-design-review': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'reject-design-review': {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    'dx-report-draft-started': {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    'create-dx': {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    'submit-dx-for-review': {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    'approve-dx-review': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'approve-dx-review-v2': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'ortho-approve-design': {
        icon: CheckBadgeIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'ortho-request-design-changes': {
        icon: ArrowUturnLeftIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle
    },
    'approve-redesign-dx-review-v2': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'approve-redesign-skip-dx-v2': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'ortho-approve-redesign': {
        icon: CheckBadgeIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'ortho-request-redesign-changes': {
        icon: ArrowUturnLeftIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle
    },
    'create-sdi-v2': {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    'ortho-approve-sdi': {
        icon: CheckBadgeIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'reject-dx-review': {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    'approve-case': {
        icon: CheckBadgeIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'request-revision': {
        icon: ArrowPathIcon,
        tone: {
            ring: 'border-yellow-300 bg-yellow-50',
            icon: 'text-yellow-700',
            dot: 'bg-yellow-500'
        },
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    // Redesign-1 forward edge: admin submits revised instructions to Spark.
    'submit-redesign': {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle,
        formatBody: designPlanBody
    },
    // GP-facing event for admin clarification requests; its engine-internal
    // counterpart is unregistered so the timeline shows the action once.
    'clarification-requested': {
        icon: ChatBubbleLeftEllipsisIcon,
        tone: TONE_INTAKE,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    // GP's response that exits Hold-5. Body shows the response text.
    'clarification-provided': {
        icon: ChatBubbleLeftEllipsisIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle,
        formatBody: clarificationProvidedBody
    },
    'decline-treatment': {
        icon: XCircleIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    // Back from a terminal status into the redesign loop (admin or practice): a positive
    // action, so the approval tone.
    'reactivate-case': {
        icon: ArrowPathIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    // Admin-only global action - only entry point is the patient-files
    // Actions tab, never a workflow-engine edge.
    'archive-case': {
        icon: ArchiveBoxIcon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    // Bulk expiry from the admin stale-designs endpoint. Internal tone: it is
    // something ODO did to the case, not something the practice chose.
    'expire-stale-design': {
        icon: ClockIcon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    'force-status': {
        icon: ExclamationTriangleIcon,
        tone: { ring: 'border-amber-300 bg-amber-50', icon: 'text-amber-700', dot: 'bg-amber-500' },
        formatTitle: defaultTitle,
        formatBody: forceStatusBody
    },
    // Ref-1 "Review for refinement" GP decision: three mutually exclusive outcomes.
    'review-approve-refinement': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'refuse-refinement-continue': {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    'refinement-resume-treatment': {
        icon: PlayCircleIcon,
        tone: TONE_SCAN,
        formatTitle: defaultTitle
    },
    'end-treatment-start-retention': {
        icon: CubeTransparentIcon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle,
        formatBody: retainerBody
    },
    'end-treatment-complete': {
        icon: CubeTransparentIcon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle,
        formatBody: retainerBody
    },
    'upload-refinement-records': {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_INTAKE,
        formatTitle: defaultTitle,
        formatBody: refinementGoalBody
    },
    'approve-refinement-records': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'reject-refinement-records': {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    'submit-refinement-for-design': {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle,
        formatBody: designPlanBody
    },
    'refinement-design-pre-approved': {
        icon: CheckBadgeIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle,
        formatBody: designPlanBody
    },
    'refinement-design-received': {
        icon: ArrowDownOnSquareIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    'approve-refinement-design-review': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'reject-refinement-design-review': {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle
    },
    'create-refinement-rdi': {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    'create-refinement-rdi-skip-approval': {
        icon: DocumentTextIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    // Ref-6.1: the GP (V1) or the orthodontist (V2) reviews the RDI.
    'approve-refinement-design': {
        icon: CheckBadgeIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'request-refinement-design-revision': {
        icon: ArrowUturnLeftIcon,
        tone: {
            ring: 'border-yellow-300 bg-yellow-50',
            icon: 'text-yellow-700',
            dot: 'bg-yellow-500'
        },
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    'submit-refinement-for-manufacturing': {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle
    },
    'refinement-shipped': {
        icon: CubeTransparentIcon,
        tone: TONE_SCAN,
        formatTitle: defaultTitle
    },
    // Ret-1 "Ready for retention" GP decision.
    'approve-retention': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'refuse-retention-continue': {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle,
        formatBody: retentionRefusalBody
    },
    'refuse-retention-end': {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle,
        formatBody: retentionRefusalBody
    },
    'submit-retention-records': {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_INTAKE,
        formatTitle: defaultTitle
    },
    'approve-retention-records': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'reject-retention-records': {
        icon: HandThumbDownIcon,
        tone: TONE_REJECTION,
        formatTitle: defaultTitle,
        formatBody: rejectionBody
    },
    'submit-retention-to-manufacturer': {
        icon: ArrowUpOnSquareIcon,
        tone: TONE_CLINICAL,
        formatTitle: defaultTitle,
        formatBody: retentionManufacturingBody
    },
    'retainers-shipped': {
        icon: CubeTransparentIcon,
        tone: TONE_SCAN,
        formatTitle: defaultTitle
    },
    'deliver-retainers': {
        icon: CheckBadgeIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    'dm-va-due': {
        icon: CalendarDaysIcon,
        tone: TONE_SCAN,
        formatTitle: defaultTitle
    },
    'clinical-va-due': {
        icon: CalendarDaysIcon,
        tone: TONE_CLINICAL,
        formatTitle: (event) => clinicalEscalationTitle(event) ?? defaultTitle(event),
        formatBody: clinicalEscalationBody
    },
    'clinical-vas-resolved': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: (event) => handBackToDmTitle(event) ?? defaultTitle(event),
        formatBody: handBackToDmBody
    },
    'vas-resolved': {
        icon: CheckCircleIcon,
        tone: TONE_APPROVAL,
        formatTitle: defaultTitle
    },
    virtual_appointment_triggered: {
        icon: CalendarDaysIcon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle,
        formatBody: vaTriggeredBody
    },
    'enter-hold': {
        icon: PauseCircleIcon,
        tone: { ring: 'border-amber-300 bg-amber-50', icon: 'text-amber-700', dot: 'bg-amber-500' },
        formatTitle: defaultTitle,
        formatBody: enterHoldBody
    },
    'set-priority': {
        icon: ExclamationTriangleIcon,
        tone: {
            ring: 'border-orange-300 bg-orange-50',
            icon: 'text-orange-700',
            dot: 'bg-orange-500'
        },
        formatTitle: defaultTitle,
        formatBody: setPriorityBody
    },
    admin_patient_updated: {
        icon: PencilSquareIcon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle,
        formatBody: adminPatientUpdateBody
    },
    admin_patient_moved_practice: {
        icon: BuildingOffice2Icon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle,
        formatBody: movePracticeBody
    },
    orthopulse_ordered: {
        icon: ShoppingCartIcon,
        tone: TONE_INTERNAL,
        formatTitle: defaultTitle,
        formatBody: orthopulseOrderedBody
    }
};

export const FALLBACK_VISUAL_CONFIG: EventVisualConfig = {
    icon: ClockIcon,
    tone: TONE_FALLBACK,
    formatTitle: defaultTitle
};

export function getEventVisualConfig(type: string): EventVisualConfig {
    return EVENT_TYPE_VISUAL_CONFIG[type] ?? FALLBACK_VISUAL_CONFIG;
}
